import { Lightbulb, ListChecks, Loader2, RefreshCw, Sparkles, StickyNote } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { createPracticeCard, type PracticeItem, type PurposePractice } from '@/api/services/book-ai-purpose'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { memoPath } from '@/routes/paths'
import { useBookAiPurposeStore } from '@/store/useBookAiPurposeStore'

import { CardCreatedNotice } from './card-created-notice'
import { CitationChip } from './citation-chip'
import { uniqueCitations } from './citation-label'
import { SimilarCardsNotice } from './similar-cards-notice'
import type { BookAiReaderState } from './useBookAi'
import { useSimilarCardsGate } from './useSimilarCardsGate'

type Props = {
  bookAi: BookAiReaderState
  practice: PurposePractice | null
  onNavigate: (key: number) => void
}

const INTRO =
  '目的に対してこの本から分かったことと、試すことをまとめます。自分のハイライトとカードも材料にし、1 項目ずつ書き換えてカードにできます'

/**
 * 実践メモ: 目的に対して、この本から分かったことと試すことをまとめ、1 項目ずつカードにする
 *
 * 材料は、目的に近い本文・自分のハイライト・この本から作ったカード（読んだ所までに絞れる）。
 */
export const PracticePanel = ({ bookAi, practice, onNavigate }: Props) => {
  const isPracticing = useBookAiPurposeStore((state) => state.isPracticing)
  const requestPractice = useBookAiPurposeStore((state) => state.requestPractice)
  const [untilRead, setUntilRead] = useState(true)

  const generate = () => {
    if (untilRead && bookAi.currentKey == null) {
      toast.info('読んでいる位置がまだ分からないため、少し読み進めてから作ってください')
      return
    }
    requestPractice(untilRead ? bookAi.currentKey : null)
  }

  return (
    <section className="space-y-2.5">
      <h3 className="text-xs font-semibold text-muted-foreground">実践メモ</h3>
      <p className="text-xs text-muted-foreground">{INTRO}</p>
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={untilRead} onCheckedChange={setUntilRead} />
          読んだ所までで作る
        </label>
        <Button size="sm" variant={practice ? 'ghost' : 'default'} disabled={isPracticing} onClick={generate}>
          {isPracticing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : practice ? (
            <RefreshCw className="size-4" />
          ) : (
            <Sparkles className="size-4" />
          )}
          {practice ? '作り直す' : '実践メモを作る'}
        </Button>
      </div>

      {practice && (
        <div className="space-y-3">
          <p className="text-[11px] text-muted-foreground">
            {new Date(practice.created_at).toLocaleDateString()} に
            {practice.until_key == null ? '本全体' : '読んだ所まで'}で作成
          </p>
          <PracticeList
            title="この本から分かったこと"
            icon={<Lightbulb className="size-3.5" />}
            items={practice.learnings}
            episodeId={bookAi.episodeId}
            onNavigate={onNavigate}
          />
          <PracticeList
            title="試すこと"
            icon={<ListChecks className="size-3.5" />}
            items={practice.actions}
            episodeId={bookAi.episodeId}
            onNavigate={onNavigate}
          />
        </div>
      )}
    </section>
  )
}

const PracticeList = ({
  title,
  icon,
  items,
  episodeId,
  onNavigate,
}: {
  title: string
  icon: React.ReactNode
  items: PracticeItem[]
  episodeId: number
  onNavigate: (key: number) => void
}) => {
  if (items.length === 0) return null
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1 text-xs font-semibold">
        {icon}
        {title}
      </p>
      <ul className="space-y-2">
        {items.map((item) => (
          <PracticeRow key={`${item.title}:${item.detail}`} item={item} episodeId={episodeId} onNavigate={onNavigate} />
        ))}
      </ul>
    </div>
  )
}

const PracticeRow = ({
  item,
  episodeId,
  onNavigate,
}: {
  item: PracticeItem
  episodeId: number
  onNavigate: (key: number) => void
}) => {
  const [editing, setEditing] = useState(false)
  const citations = uniqueCitations(item.citations)
  return (
    <li className="space-y-1.5 rounded-lg bg-muted/60 p-2.5 text-sm">
      <p className="font-medium wrap-break-word">{item.title}</p>
      <p className="text-xs leading-relaxed text-muted-foreground wrap-break-word">{item.detail}</p>
      {item.citations.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {citations.map((citation) => (
            <CitationChip key={citation.chunk_id} citation={citation} onClick={() => onNavigate(citation.key)} />
          ))}
        </div>
      )}
      {editing ? (
        <PracticeCardEditor item={item} episodeId={episodeId} />
      ) : (
        <Button variant="ghost" size="sm" className="h-7 px-2! text-xs" onClick={() => setEditing(true)}>
          <StickyNote className="size-3.5" />
          カードにする
        </Button>
      )}
    </li>
  )
}

/** 実践メモの 1 項目を書き換えてカードにする（出典の本・読む目的・根拠の本文が入る） */
const PracticeCardEditor = ({ item, episodeId }: { item: PracticeItem; episodeId: number }) => {
  const navigate = useNavigate()
  const [title, setTitle] = useState(item.title)
  const [body, setBody] = useState(item.detail)
  const [includeQuotes, setIncludeQuotes] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [createdMemoId, setCreatedMemoId] = useState<number | null>(null)
  const similarGate = useSimilarCardsGate()

  const create = async () => {
    if (!(await similarGate.check(`${title}\n${body}`))) return
    setIsCreating(true)
    try {
      const card = await createPracticeCard(episodeId, {
        title: title.trim(),
        body,
        citation_chunk_ids: includeQuotes ? item.citations.map((c) => c.chunk_id) : [],
        link_memo_ids: similarGate.linkIds,
      })
      setCreatedMemoId(card.memo_id)
      toast.success('カードを作りました', {
        action: { label: '開く', onClick: () => navigate(memoPath(card.memo_id)) },
      })
    } catch (error) {
      console.error('カードを作れませんでした:', error)
      toast.error('カードを作れませんでした')
    } finally {
      setIsCreating(false)
    }
  }

  if (createdMemoId != null) return <CardCreatedNotice memoId={createdMemoId} className="bg-background p-2" />

  return (
    <div className="space-y-2 rounded-lg border bg-background p-2.5">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={255}
        aria-label="カードの題"
        className="text-sm"
      />
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        maxLength={4000}
        aria-label="カードの本文"
        className="resize-none text-sm"
      />
      {item.citations.length > 0 && (
        <label className="flex items-center gap-2 text-xs">
          <Checkbox checked={includeQuotes} onCheckedChange={(value) => setIncludeQuotes(value === true)} />
          根拠の本文を引用する（{item.citations.length}）
        </label>
      )}
      <SimilarCardsNotice gate={similarGate} />
      <Button
        size="sm"
        className="w-full"
        disabled={!title.trim() || isCreating || similarGate.isChecking}
        onClick={create}
      >
        {isCreating || similarGate.isChecking ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <StickyNote className="size-4" />
        )}
        {similarGate.similar?.length ? 'このままカードにする' : 'カードにする'}
      </Button>
    </div>
  )
}
